export type AttentionItem = {
  id: string
  title: string
  detail: string
  impact?: string
  action: string
  target: 'inventory' | 'performance' | 'controls' | 'messages' | 'finances' | 'marketing'
}

export type Recommendation = {
  observation: string
  title: string
  reasoning: string
  impact: string
  action: string
  target: 'inventory' | 'performance' | 'controls' | 'marketing'
}

export type ActivityEntry = {
  when: string
  detail: string
}

const eddyAttention: AttentionItem[] = [
  {
    id: 'eddy-plan',
    title: 'Approve summer recovery plan',
    detail: 'Carli recommends activating 142 units across 3 approved recovery channels.',
    impact: 'Projected recovery: $18,400',
    action: 'Review plan',
    target: 'controls',
  },
  {
    id: 'eddy-message',
    title: 'New message from ManyCo',
    detail: 'Your account team asked whether more Spring Collection excess is coming in June.',
    action: 'Open message',
    target: 'messages',
  },
]

const nisoloAttention: AttentionItem[] = []

const onePeopleAttention: AttentionItem[] = [
  {
    id: 'one-people-channel',
    title: 'Channel approval needed',
    detail: 'Carli recommends adding Vinted for 318 units that are moving slowly on existing channels.',
    impact: 'Projected recovery: $9,200',
    action: 'Review channel',
    target: 'controls',
  },
]

export const attentionFor = (brand: string): AttentionItem[] => {
  if (brand === 'Eddy') return eddyAttention
  if (brand === 'Nisolo') return nisoloAttention
  if (brand === '1 People') return onePeopleAttention
  return []
}

export const recommendationFor = (brand: string): Recommendation | null => {
  if (brand === 'Eddy') {
    return {
      observation: 'Carli sees 187 units recovering below forecast on their current channels.',
      title: 'Increase exposure on 187 slower-moving units',
      reasoning:
        'These products are recovering below forecast where they sit today. Carli recommends expanding them to eBay and Whatnot while maintaining Eddy’s existing pricing rules.',
      impact: '+$8,400 expected incremental recovery',
      action: 'Review recommendation',
      target: 'controls',
    }
  }
  if (brand === 'Nisolo') {
    return {
      observation: 'Imperfects are currently limited to Manymoons and Whatnot.',
      title: 'Open the Imperfects category to more channels',
      reasoning:
        'The 472 Imperfects lot is reaching only a fraction of the resale audience on its two approved channels. Carli recommends approving additional marketplaces for this category to widen reach and accelerate sell-through without moving off your pricing limit.',
      impact: '5–10% lift in monthly sales',
      action: 'Review channels',
      target: 'controls',
    }
  }
  if (brand === '1 People') {
    return {
      observation: 'Carli sees 318 units with no sales in the last 30 days.',
      title: 'Reprice 318 stalled units before the next cycle',
      reasoning:
        'These units have had full exposure without converting, which suggests price rather than visibility is the constraint. Carli recommends a single step down within your approved floor instead of broader discounting.',
      impact: '+$6,100 expected incremental recovery',
      action: 'Review recommendation',
      target: 'controls',
    }
  }
  return null
}

const eddyActivity: ActivityEntry[] = [
  { when: 'Today', detail: '17 units sold across Manymoons and eBay' },
  { when: 'Yesterday', detail: 'June inventory upload completed' },
  { when: 'Aug 17', detail: 'Summer recovery plan approved' },
  { when: 'Aug 15', detail: '$5,061 payout issued' },
]

const nisoloActivity: ActivityEntry[] = [
  { when: 'Today', detail: '34 units sold across Manymoons and Poshmark' },
  { when: 'Apr 22', detail: '473 — Damages submission received' },
  { when: 'Mar 04', detail: 'Recovery pricing rules updated' },
  { when: 'Feb 15', detail: '472 — Imperfects submission received' },
]

const onePeopleActivity: ActivityEntry[] = [
  { when: 'Today', detail: '9 units sold on Manymoons' },
  { when: 'Aug 12', detail: 'Channel mix updated to include Whatnot' },
  { when: 'Aug 04', detail: '$2,480 payout issued' },
]

export const activityFor = (brand: string): ActivityEntry[] => {
  if (brand === 'Eddy') return eddyActivity
  if (brand === 'Nisolo') return nisoloActivity
  if (brand === '1 People') return onePeopleActivity
  return []
}

export const activePrompts = [
  'What inventory needs attention?',
  'What’s selling fastest?',
  'Which channels are performing best?',
  'Why did recovery change this month?',
]

export const onboardingPrompts = [
  'What inventory should I send first?',
  'What information do you need?',
  'How does recovery pricing work?',
  'Which channels could fit my brand?',
]
